import axios from 'axios';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleCloseDropdownOnBlur,
    handleCloseInputDropdownOnBlur,
    normalizeModelName,
    setCaptchaEuScript,
} from '../utils';
import { isCaptchaEnabled } from '../services/captcha';
import { getFormRenderToken } from '../services/anti-bot';

const REQUEST_TYPE_OFFER = 'offer';
const REQUEST_TYPE_TEST_DRIVE = 'testDrive';

const REQUEST_TYPE_ACTIONS = {
    [REQUEST_TYPE_OFFER]: 'mg-business-logic/request-offer/create',
    [REQUEST_TYPE_TEST_DRIVE]: 'mg-business-logic/request-test-drive/create',
};

const REQUEST_TYPE_TRACKING = {
    [REQUEST_TYPE_OFFER]: {
        leadType: 'Finance Quote',
        event: 'configuration',
    },
    [REQUEST_TYPE_TEST_DRIVE]: {
        leadType: 'Test Drive',
        event: 'request test drive',
    },
};

Vue.component('event-lead-form-component', {
    props: {
        allStores: {
            value: Array,
        },
        siteHandle: {
            value: String,
        },
        defaultModel: {
            value: String,
        },
        captchaEuPublicKey: {
            value: String,
        },
    },

    data() {
        return {
            formRenderToken: '',
            stores: this.allStores,
            errors: new Errors(),
            submitting: false,
            selectedBrandStore: null,
            countryHandle: this.siteHandle,
            model: '',
            newsletterSignUp: false,

            leadRequestType: '',
            leadRequestTypeLabel: '',
        };
    },

    computed: {
        orderedStores() {
            return _orderBy(this.stores, 'title');
        },

        isSubmitLoading() {
            return this.submitting ? 'button--loading' : '';
        },

        isOfferRequest() {
            return this.leadRequestType === REQUEST_TYPE_OFFER;
        },

        isTestDriveRequest() {
            return this.leadRequestType === REQUEST_TYPE_TEST_DRIVE;
        },

        formAction() {
            return REQUEST_TYPE_ACTIONS[this.leadRequestType] ?? '';
        },
    },

    mounted() {
        this.model = this.defaultModel;

        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });

        handleCloseDropdownOnBlur('.new-input__dropdown-container--model');
        handleCloseDropdownOnBlur('#event-request-type-dropdown');
        handleCloseInputDropdownOnBlur(
            '#store-dropdown-container',
            '#searchBrandstore',
        );

        EventBus.$on('brandstore-selected', (brandstoreValue) => {
            this.selectedBrandStore = brandstoreValue;
        });
    },

    methods: {
        handleMarketingConsentChange(e) {
            e.preventDefault();
            this.newsletterSignUp = e.target.checked;
        },

        setDropdownRequestType(event) {
            this.leadRequestType = event.currentTarget.dataset.value;
            this.leadRequestTypeLabel = event.currentTarget.dataset.text;
            const { eventRequestType, ...otherErrors } = this.errors.errors;
            this.errors.record(otherErrors);
            this.toggleDropdown(event);
        },

        async onSubmit(event) {
            event.preventDefault();

            if (this.submitting) return;

            if (!this.formAction) {
                this.errors.record({ eventRequestType: ['blank'] });
                return;
            }

            this.submitting = true;

            const form = event.currentTarget;
            const dataLayerData = this.buildDataLayerData(
                Object.fromEntries(new FormData(form).entries()),
            );

            try {
                const captchaSolution = (await isCaptchaEnabled())
                    ? await KROT.getSolution(this.captchaEuPublicKey)
                    : null;

                await this.handleDataSubmit(
                    form,
                    dataLayerData,
                    captchaSolution,
                );
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, dataLayerData, captchaSolution) {
            const formData = new FormData(form);
            formData.append('sourceUrl', window.location.href);
            formData.append('formRenderToken', this.formRenderToken);

            if (captchaSolution) {
                formData.append(
                    'captchaSolution',
                    JSON.stringify(captchaSolution),
                );
            }

            const result = await this.postForm(formData);

            if (result.data?.errors) {
                this.errors.record(result.data.errors);
                return;
            }

            this.errors.clear();
            this.sendGTMData(dataLayerData);

            setTimeout(() => {
                window.location = result.data.redirectUrl;
            }, 1000);
        },

        async postForm(formData) {
            try {
                const response = await axios.post('/', formData);
                return response.data;
            } catch (e) {
                if (!e.response?.data?.data?.errors) throw e;
                return e.response.data;
            }
        },

        buildDataLayerData(data) {
            return {
                event: 'lead',
                lead_type: REQUEST_TYPE_TRACKING[this.leadRequestType].leadType,
                client_type: 'B2C',
                brandstore: this.selectedBrandStore
                    ? this.selectedBrandStore.title
                    : '',
                payment_type: '',
                car: {
                    name: normalizeModelName(this.model, this.siteHandle),
                    ids: [],
                    edition: '',
                    color: '',
                },
                user_data: {
                    email_address: data.emailAddress,
                    phone_number: data.phone,
                    address: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                    },
                },
            };
        },

        sendGTMData(dataLayerData) {
            if (!checkCCM19Consent() && !checkOneTrustConsent()) return;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(dataLayerData);
            window.dataLayer.push({
                event: REQUEST_TYPE_TRACKING[this.leadRequestType].event,
                model: normalizeModelName(this.model, this.siteHandle),
                edition: ' ',
                page_path: window.location.href,
                country: `${this.siteHandle} - ${this.$root.languageCode}`,
            });
        },

        toggleDropdown(event) {
            event.preventDefault();
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },
    },
});
